import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  Settings, 
  LogOut, 
  LayoutDashboard,
  BookOpen,
  Users,
  FileEdit,
  Shield,
  Crown,
  ChevronDown,
  Home,
  Search,
  Bookmark,
  Award,
  BarChart3,
  Globe,
  Target
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface UserButtonProps {
  className?: string;
  variant?: 'default' | 'minimal' | 'compact';
}

export function UserButton({ className = '', variant = 'default' }: UserButtonProps) {
  const { user, profile, signOut } = useAuth();
  const roleAccess = useRoleAccess();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  if (!user || !profile) {
    return (
      <Button 
        variant="outline" 
        onClick={() => navigate('/auth/login')}
        className={className}
      >
        <User className="h-4 w-4 mr-2" />
        Sign In
      </Button>
    );
  }

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
      toast.success('Signed out successfully');
    } catch (error) {
      toast.error('Error signing out');
    }
    setIsOpen(false);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin': return <Crown className="h-3 w-3" />;
      case 'admin': return <Shield className="h-3 w-3" />;
      case 'editor': return <FileEdit className="h-3 w-3" />;
      case 'contributor': return <BookOpen className="h-3 w-3" />;
      default: return <User className="h-3 w-3" />;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'super_admin': return 'destructive';
      case 'admin': return 'destructive';
      case 'editor': return 'default';
      case 'contributor': return 'secondary';
      default: return 'outline';
    }
  };

  const getNavigationItems = () => {
    const nav: Array<{ 
      icon: React.ReactNode; 
      label: string; 
      path: string; 
      separator?: boolean;
      description?: string;
    }> = [];

    // Universal navigation items
    nav.push(
      { icon: <Home className="h-4 w-4" />, label: 'Home', path: '/', description: 'Main website' },
      { icon: <Globe className="h-4 w-4" />, label: 'Explore', path: '/search', description: 'Browse content' },
      { icon: <Target className="h-4 w-4" />, label: 'Convergence', path: '/convergence', description: 'Cross-disciplinary learning' }
    );

    // Role-based dashboards using the role access system
    const availableNavItems = roleAccess.getAvailableNavItems();
    
    if (availableNavItems.length > 0) {
      nav.push({ separator: true, icon: null, label: '', path: '' });
      
      availableNavItems.forEach(item => {
        let icon: React.ReactNode;
        
        switch (item.id) {
          case 'dashboard':
            icon = <LayoutDashboard className="h-4 w-4" />;
            break;
          case 'content':
            icon = <BookOpen className="h-4 w-4" />;
            break;
          case 'editorial':
            icon = <FileEdit className="h-4 w-4" />;
            break;
          case 'admin':
            icon = <Shield className="h-4 w-4" />;
            break;
          default:
            icon = <LayoutDashboard className="h-4 w-4" />;
        }
        
        nav.push({
          icon,
          label: item.label,
          path: item.path,
          description: item.description
        });
      });
    }

    // Personal learning tools (available to all authenticated users)
    nav.push(
      { separator: true, icon: null, label: '', path: '' },
      { icon: <Bookmark className="h-4 w-4" />, label: 'Bookmarks', path: '/dashboard?tab=bookmarks', description: 'Saved content' },
      { icon: <Award className="h-4 w-4" />, label: 'Achievements', path: '/dashboard?tab=achievements', description: 'Your progress badges' },
      { icon: <BarChart3 className="h-4 w-4" />, label: 'Progress', path: '/dashboard?tab=progress', description: 'Learning analytics' }
    );

    return nav;
  };

  if (variant === 'minimal') {
    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={`h-8 w-8 rounded-full ${className}`}>
            <Avatar className="h-7 w-7">
              <AvatarImage src={profile.avatar_url} alt={profile.full_name || 'User'} />
              <AvatarFallback className="text-xs">
                {profile.full_name?.charAt(0)?.toUpperCase() || profile.username?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-black">
                  {profile.full_name || profile.username || 'User'}
                </p>
                <Badge variant={getRoleBadgeVariant(profile.role)} className="text-xs flex items-center gap-1">
                  {getRoleIcon(profile.role)}
                  {profile.role.replace('_', ' ')}
                </Badge>
              </div>
              <p className="text-xs text-gray-600">{user.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {getNavigationItems().map((item, index) => {
            if (item.separator) {
              return <DropdownMenuSeparator key={index} />;
            }
            return (
              <DropdownMenuItem 
                key={index} 
                onClick={() => handleNavigation(item.path)}
                className="cursor-pointer flex flex-col items-start"
              >
                <div className="flex items-center w-full">
                  {item.icon}
                  <span className="ml-2 font-medium">{item.label}</span>
                </div>
                {item.description && (
                  <span className="text-xs text-gray-500 ml-6">{item.description}</span>
                )}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleNavigation('/settings')} className="cursor-pointer">
            <Settings className="h-4 w-4" />
            <span className="ml-2">Settings</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600">
            <LogOut className="h-4 w-4" />
            <span className="ml-2">Sign Out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (variant === 'compact') {
    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className={`h-9 px-2 ${className}`}>
            <Avatar className="h-6 w-6 mr-2">
              <AvatarImage src={profile.avatar_url} alt={profile.full_name || 'User'} />
              <AvatarFallback className="text-xs">
                {profile.full_name?.charAt(0)?.toUpperCase() || profile.username?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium max-w-24 truncate">
              {profile.full_name || profile.username || 'User'}
            </span>
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-black">
                  {profile.full_name || profile.username || 'User'}
                </p>
                <Badge variant={getRoleBadgeVariant(profile.role)} className="text-xs flex items-center gap-1">
                  {getRoleIcon(profile.role)}
                  {profile.role.replace('_', ' ')}
                </Badge>
              </div>
              <p className="text-xs text-gray-600">{user.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {getNavigationItems().map((item, index) => {
            if (item.separator) {
              return <DropdownMenuSeparator key={index} />;
            }
            return (
              <DropdownMenuItem 
                key={index} 
                onClick={() => handleNavigation(item.path)}
                className="cursor-pointer flex flex-col items-start"
              >
                <div className="flex items-center w-full">
                  {item.icon}
                  <span className="ml-2 font-medium">{item.label}</span>
                </div>
                {item.description && (
                  <span className="text-xs text-gray-500 ml-6">{item.description}</span>
                )}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleNavigation('/settings')} className="cursor-pointer">
            <Settings className="h-4 w-4" />
            <span className="ml-2">Settings</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600">
            <LogOut className="h-4 w-4" />
            <span className="ml-2">Sign Out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default variant - full display
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className={`h-10 px-3 justify-start ${className}`}>
          <Avatar className="h-8 w-8 mr-3">
            <AvatarImage src={profile.avatar_url} alt={profile.full_name || 'User'} />
            <AvatarFallback>
              {profile.full_name?.charAt(0)?.toUpperCase() || profile.username?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium">
              {profile.full_name || profile.username || 'User'}
            </span>
            <div className="flex items-center gap-1">
              <Badge variant={getRoleBadgeVariant(profile.role)} className="text-xs flex items-center gap-1">
                {getRoleIcon(profile.role)}
                {profile.role.replace('_', ' ')}
              </Badge>
            </div>
          </div>
          <ChevronDown className="h-4 w-4 ml-auto" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-black">
                {profile.full_name || profile.username || 'User'}
              </p>
              <Badge variant={getRoleBadgeVariant(profile.role)} className="text-xs flex items-center gap-1">
                {getRoleIcon(profile.role)}
                {profile.role.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-xs text-gray-600">{user.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {getNavigationItems().map((item, index) => {
          if (item.separator) {
            return <DropdownMenuSeparator key={index} />;
          }
          return (
            <DropdownMenuItem 
              key={index} 
              onClick={() => handleNavigation(item.path)}
              className="cursor-pointer flex flex-col items-start py-2"
            >
              <div className="flex items-center w-full">
                {item.icon}
                <span className="ml-2 font-medium">{item.label}</span>
              </div>
              {item.description && (
                <span className="text-xs text-gray-500 ml-6">{item.description}</span>
              )}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleNavigation('/settings')} className="cursor-pointer">
          <Settings className="h-4 w-4" />
          <span className="ml-2">Settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600">
          <LogOut className="h-4 w-4" />
          <span className="ml-2">Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 